import {
  closestCenter,
  defaultDropAnimationSideEffects,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DropAnimation,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button } from 'components/atoms';
import EmptyPlaceholder from 'components/atoms/empty/EmptyPlaceholder';
import ArtistRoleCard from 'components/molecules/card/artist-role/ArtistRoleCard';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PiPlus } from 'react-icons/pi';
import { ARTIST_ROLE, IArtistWithRoleForm, STATUS } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { measuring } from 'utils/dnd/dndUtilities';

export const artistRoleValues: IArtistWithRoleForm = {
  nickname: '',
  id: '',
  role: ARTIST_ROLE.unknown,
  artistStatus: STATUS.draft,
  isDuplicated: false,
  watchListArtist: false,
  previewPicture: undefined,
};

const ArtistsRoleTab = <Form extends Record<string, IArtistWithRoleForm[]>>({
  propertyName = 'artists',
}: {
  propertyName?: string;
}) => {
  const { values, status, setFieldValue }: IFormikContextType<Form> = useFormikContext<Form>();

  const { t } = useTranslation();

  const sortableItems = useMemo(
    () =>
      values[propertyName].map((artist) => ({
        ...artist,
        id: `${artist.id}-${artist.role}`,
        artistId: artist.id,
      })),
    [values[propertyName]],
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const dropAnimationConfig: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.8',
        },
      },
    }),
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    document.body.style.setProperty('cursor', 'grabbing');
  };

  const resetState = () => {
    document.body.style.setProperty('cursor', '');
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
  };
  const findIndexById = (id: UniqueIdentifier) =>
    values[propertyName].findIndex(
      (artist) =>
        artist.id === `${id}`.split('-').splice(0, 5).join('-') &&
        `${artist.role}` === `${id}`.split('-').splice(5, 1).join('-'),
    );
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      console.log(`${active.id}`.split('-').splice(0, 5).join('-'));
      const movedArray = arrayMove(
        values[propertyName],
        findIndexById(active.id),
        findIndexById(over.id),
      );

      setFieldValue(propertyName, movedArray);
    }
    document.body.style.setProperty('cursor', '');
  };

  return (
    <div className="flex flex-col w-full gap-4">
      {!status?.displayMode && (
        <Button
          fullWidth
          variant={BUTTON_VARIANT.white}
          onClick={() =>
            setFieldValue(propertyName, [...(values[propertyName] ?? []), artistRoleValues])
          }
        >
          <Button.Icon icon={<PiPlus />} />
          <Button.Text text={t('button.add')} />
        </Button>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        measuring={measuring}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={resetState}
      >
        <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
          {sortableItems.length ? (
            sortableItems.map((artist, index) => (
              <ArtistRoleCard
                key={index}
                index={index}
                artist={{ ...artist, id: artist.artistId }}
                sortableKey={artist.id}
                name={propertyName}
                displayMode={status?.displayMode}
                onRemove={() =>
                  setFieldValue(
                    propertyName,
                    values[propertyName].filter((_, titleIndex) => index !== titleIndex),
                  )
                }
              />
            ))
          ) : (
            <EmptyPlaceholder />
          )}
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default ArtistsRoleTab;
